import { createApiClient, ApiClientError } from '@warmancer/ui';
import { useAdminStore } from '../stores/adminStore';
import type { AdminRole } from '@warmancer/shared';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const api = createApiClient({
  baseUrl: API_BASE_URL,
  onUnauthorized: () => {
    useAdminStore.getState().clearSession();
  },
  getAuthToken: () => useAdminStore.getState().token,
});

export { ApiClientError };

// Auth API
export interface LoginRequest {
  role: AdminRole;
  password: string;
  captcha_token?: string;
}

export interface LoginResponse {
  token: string;
  role: AdminRole;
  expires_at: string;
}

export const authApi = {
  login: (data: LoginRequest) =>
    api.post<LoginResponse>('/auth', data),

  logout: () =>
    api.post('/auth/logout'),

  getSession: () =>
    api.get<{ role: AdminRole; expires_at: string }>('/auth/session'),
};

// Accounts API
export interface Account {
  id: string;
  username: string;
  username_display: string;
  email: string;
  email_verified: boolean;
  fangs_balance: number;
  has_purchased: boolean;
  status: string;
  discord_id: string | null;
  avatar_url: string | null;
  referred_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface AccountsListResponse {
  accounts: Account[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export const accountsApi = {
  list: (params?: { search?: string; status?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    return api.get<AccountsListResponse>(`/accounts?${query.toString()}`);
  },

  get: (id: string) =>
    api.get<{ account: Account; moderation_history: ModerationAction[] }>(`/accounts/${id}`),

  modifyFangs: (id: string, amount: number, reason: string) =>
    api.post(`/accounts/${id}/fangs`, { amount, reason }),

  getDevices: (id: string) =>
    api.get<{ devices: DeviceAccount[] }>(`/accounts/${id}/devices`),

  clearDevices: (id: string) =>
    api.delete<{ deleted: number }>(`/accounts/${id}/devices`),

  deleteAccount: (id: string) =>
    api.delete<{ deleted: boolean }>(`/accounts/${id}`),

  updateAccount: (id: string, data: { email?: string; email_verified?: boolean; username?: string }) =>
    api.patch<{ updated: boolean; emailSent?: boolean; emailError?: string }>(`/accounts/${id}`, data),

  checkUsername: (username: string, excludeId: string) =>
    api.get<{ available: boolean; error?: string }>(
      `/accounts/check-username?username=${encodeURIComponent(username)}&exclude_id=${encodeURIComponent(excludeId)}`
    ),
};

// Device types
export interface DeviceAccount {
  id: string;
  visitor_id: string;
  request_id: string;
  confidence: number | null;
  first_seen_at: string;
  last_seen_at: string;
}

export interface DeviceExclusion {
  id: string;
  visitor_id: string;
  reason: string;
  created_by: string;
  created_at: string;
}

export interface ExclusionsListResponse {
  exclusions: DeviceExclusion[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

// Devices API
export const devicesApi = {
  listExclusions: (params?: { limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    return api.get<ExclusionsListResponse>(`/devices/exclusions?${query.toString()}`);
  },

  addExclusion: (data: { visitor_id: string; reason: string }) =>
    api.post<{ id: string }>('/devices/exclusions', data),

  removeExclusion: (id: string) =>
    api.delete(`/devices/exclusions/${id}`),
};

// Moderation API
export interface ModerationAction {
  id: string;
  account_id: string;
  action_type: string;
  reason: string;
  evidence: string | null;
  duration_minutes: number | null;
  issued_by: string;
  expires_at: string | null;
  created_at: string;
}

export const moderationApi = {
  ban: (id: string, data: { reason: string; duration_minutes?: number; evidence?: string }) =>
    api.post(`/moderation/ban/${id}`, data),

  unban: (id: string) =>
    api.post(`/moderation/unban/${id}`),

  mute: (id: string, data: { reason: string; duration_minutes: number; evidence?: string }) =>
    api.post(`/moderation/mute/${id}`, data),

  unmute: (id: string) =>
    api.post(`/moderation/unmute/${id}`),

  warn: (id: string, data: { reason: string; evidence?: string }) =>
    api.post(`/moderation/warn/${id}`, data),

  getHistory: (id: string) =>
    api.get<{ actions: ModerationAction[] }>(`/moderation/history/${id}`),
};

// Audit API
export interface AuditEntry {
  id: string;
  admin_role: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
}

export interface AuditListResponse {
  entries: AuditEntry[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export const auditApi = {
  list: (params?: { action?: string; target_type?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.action) query.set('action', params.action);
    if (params?.target_type) query.set('target_type', params.target_type);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    return api.get<AuditListResponse>(`/audit?${query.toString()}`);
  },

  get: (id: string) =>
    api.get<AuditEntry>(`/audit/${id}`),

  getActions: () =>
    api.get<{ actions: string[] }>('/audit/actions/list'),
};

// Errors API
export interface ErrorLog {
  id: string;
  source: string;
  level: string;
  message: string;
  stack_trace: string | null;
  metadata: Record<string, unknown> | null;
  user_agent: string | null;
  ip_address: string | null;
  url: string | null;
  resolved: boolean;
  created_at: string;
}

export interface ErrorsListResponse {
  errors: ErrorLog[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface ErrorStats {
  total: number;
  unresolved: number;
  bySource: Array<{ source: string; count: number }>;
  byLevel: Array<{ level: string; count: number }>;
  last7Days: Array<{ date: string; count: number }>;
}

export const errorsApi = {
  list: (params?: { source?: string; level?: string; resolved?: boolean; limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.source) query.set('source', params.source);
    if (params?.level) query.set('level', params.level);
    if (params?.resolved !== undefined) query.set('resolved', params.resolved.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    return api.get<ErrorsListResponse>(`/errors?${query.toString()}`);
  },

  get: (id: string) =>
    api.get<ErrorLog>(`/errors/${id}`),

  getStats: () =>
    api.get<ErrorStats>('/errors/stats'),

  resolve: (id: string, resolved: boolean) =>
    api.patch(`/errors/${id}`, { resolved }),

  delete: (id: string) =>
    api.delete(`/errors/${id}`),

  bulkDelete: (olderThanDays: number) =>
    api.delete<{ deleted: number }>(`/errors?older_than_days=${olderThanDays}`),

  purge: (params?: { source?: string; level?: string; resolved?: string }) => {
    const query = new URLSearchParams();
    if (params?.source) query.set('source', params.source);
    if (params?.level) query.set('level', params.level);
    if (params?.resolved) query.set('resolved', params.resolved);
    return api.delete<{ deleted: number }>(`/errors/purge?${query.toString()}`);
  },

  exportAll: (params?: { source?: string; level?: string; resolved?: string }) => {
    const query = new URLSearchParams();
    if (params?.source) query.set('source', params.source);
    if (params?.level) query.set('level', params.level);
    if (params?.resolved) query.set('resolved', params.resolved);
    return api.get<{ errors: ErrorLog[] }>(`/errors/export?${query.toString()}`);
  },
};

// Request Logs API
export interface RequestLog {
  id: string;
  worker: string;
  method: string;
  path: string;
  status_code: number;
  duration_ms: number;
  ip_address: string | null;
  account_id: string | null;
  account_username: string | null;
  fingerprint: string | null;
  user_agent: string | null;
  error_code: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface RequestLogsListResponse {
  logs: RequestLog[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface RequestLogStats {
  total: number;
  byWorker: Array<{ worker: string; count: number }>;
  byStatus: Array<{ status_group: string; count: number }>;
  last7Days: Array<{ date: string; count: number }>;
  avgDuration: number;
}

export interface RequestLogFilters {
  ip?: string;
  account?: string;
  fingerprint?: string;
  worker?: string;
  method?: string;
  status_min?: string;
  status_max?: string;
  path?: string;
  error_code?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

// Settings API
export type FingerprintCheckValue = boolean | 'low' | 'medium' | 'high' | number;

export interface NumericCheckConfig {
  min: number;
  max: number;
  step: number;
  default: number;
  unit: string;
}

export interface FingerprintChecksSettings {
  botd: FingerprintCheckValue;
  tampering: FingerprintCheckValue;
  lowConfidence: FingerprintCheckValue;
  vpn: FingerprintCheckValue;
  tor: FingerprintCheckValue;
  proxy: FingerprintCheckValue;
  ipBlocklist: FingerprintCheckValue;
  virtualMachine: FingerprintCheckValue;
  suspectScore: FingerprintCheckValue;
  incognito: FingerprintCheckValue;
  developerTools: FingerprintCheckValue;
  remoteControl: FingerprintCheckValue;
  locationSpoofing: FingerprintCheckValue;
  emulator: FingerprintCheckValue;
  frida: FingerprintCheckValue;
  clonedApp: FingerprintCheckValue;
}

export interface FingerprintSettingsResponse {
  settings: FingerprintChecksSettings;
  descriptions: Record<keyof FingerprintChecksSettings, string>;
  confidenceChecks: string[];
  numericChecks: Record<string, NumericCheckConfig>;
  updated_at: string | null;
}

export interface FingerprintSettingsUpdateResponse {
  settings: FingerprintChecksSettings;
  changes: Record<string, { old: FingerprintCheckValue; new: FingerprintCheckValue }>;
}

export const settingsApi = {
  getFingerprintSettings: () =>
    api.get<FingerprintSettingsResponse>('/settings/fingerprint'),

  updateFingerprintSettings: (settings: Partial<FingerprintChecksSettings>) =>
    api.patch<FingerprintSettingsUpdateResponse>('/settings/fingerprint', settings),

  resetFingerprintDefaults: () =>
    api.post<FingerprintSettingsUpdateResponse>('/settings/fingerprint/reset-defaults'),
};

export const requestLogsApi = {
  list: (params?: RequestLogFilters) => {
    const query = new URLSearchParams();
    if (params?.ip) query.set('ip', params.ip);
    if (params?.account) query.set('account', params.account);
    if (params?.fingerprint) query.set('fingerprint', params.fingerprint);
    if (params?.worker) query.set('worker', params.worker);
    if (params?.method) query.set('method', params.method);
    if (params?.status_min) query.set('status_min', params.status_min);
    if (params?.status_max) query.set('status_max', params.status_max);
    if (params?.path) query.set('path', params.path);
    if (params?.error_code) query.set('error_code', params.error_code);
    if (params?.start_date) query.set('start_date', params.start_date);
    if (params?.end_date) query.set('end_date', params.end_date);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    return api.get<RequestLogsListResponse>(`/request-logs?${query.toString()}`);
  },

  get: (id: string) =>
    api.get<RequestLog>(`/request-logs/${id}`),

  getStats: () =>
    api.get<RequestLogStats>('/request-logs/stats'),

  exportAll: (params?: RequestLogFilters) => {
    const query = new URLSearchParams();
    if (params?.ip) query.set('ip', params.ip);
    if (params?.account) query.set('account', params.account);
    if (params?.fingerprint) query.set('fingerprint', params.fingerprint);
    if (params?.worker) query.set('worker', params.worker);
    if (params?.method) query.set('method', params.method);
    if (params?.status_min) query.set('status_min', params.status_min);
    if (params?.status_max) query.set('status_max', params.status_max);
    if (params?.start_date) query.set('start_date', params.start_date);
    if (params?.end_date) query.set('end_date', params.end_date);
    return api.get<{ logs: RequestLog[] }>(`/request-logs/export?${query.toString()}`);
  },

  cleanup: (days?: number) =>
    api.delete<{ deleted: number }>(`/request-logs/cleanup?days=${days || 90}`),
};
